import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, ref } from 'firebase/database';
import { database } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { profileFromRecord } from '../../services/profileData';
import { page, wrap, card, h1, h2, muted, label, input, row, bigRow, backBtn, money, num, table, th, td } from './ui';
import { splitRecords } from '../../services/cmaComps';
import { readRecord, featureRows, runAnalysis, missingValues, extrasFor, tierFor } from '../../services/cmaFeatures';
import { sendCmaToClient } from '../../services/cmaDelivery';
import { uploadDealDocument } from '../../services/dealDocuments';
import { watchAccessible, canSeeRecord } from '../../services/recordAccess';
import { getSellerInterest, linkCmaToInterest, SI_PATH, fullName, ownsInterest } from '../../services/sellerInterestService';
import { SAMPLE_PIPELINE, SHOW_SAMPLES } from '../../data/samplePipeline';
import { priceRange } from '../../services/pocketListingService';
import { saveCmaDraft, clientLinks } from '../../services/cmaDrafts';
import { ref as dbRef2, get as dbGet2 } from 'firebase/database';

const toNumber = (s) => {
  const n = parseFloat(String(s ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const btnGold = { backgroundColor: '#D4AF37', color: '#1a1a1a', border: 'none', padding: '10px 18px', borderRadius: 8, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' };
const btnOutline = { backgroundColor: '#ffffff', color: '#1a1a1a', border: '1px solid #D4AF37', padding: '10px 18px', borderRadius: 8, fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' };
const btnDisabled = { opacity: 0.5, cursor: 'not-allowed' };
const xBtn = { background: 'none', border: '1px solid #d8c9a8', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', color: '#8C6D1F', fontWeight: 700 };

const SWATCH = { sold: '#2E9E5B', active: '#8a8a8a', pending: '#2F5FB3', rec: '#B8962E' };

const loadScript = (src, global) => new Promise((resolve, reject) => {
  if (window[global]) return resolve(window[global]);
  const el = document.createElement('script');
  el.src = src;
  el.onload = () => (window[global] ? resolve(window[global]) : reject(new Error('Library did not load')));
  el.onerror = () => reject(new Error('Could not load a required library. Check your internet connection.'));
  document.head.appendChild(el);
});

const loadPdfJs = async () => {
  const lib = await loadScript('https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js', 'pdfjsLib');
  lib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  return lib;
};

const readPdfText = async (file) => {
  const pdfjs = await loadPdfJs();
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  let out = '';
  for (let i = 1; i <= doc.numPages; i += 1) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    content.items.forEach((item) => { out += item.str + (item.hasEOL ? '\n' : ' '); });
    out += '\n';
  }
  return out;
};

// Property History report rows: MLS, DOM, price, change type, date.
const HISTORY_ROW = /(\d{7,10})\s+\S+\s+(\d+|--)\s+\$([\d,]+)\s+(New Listing|Price Decrease|Pending|Back On Market|Extended|Sold|Rented|Terminated|Withdrawn|Expired|Canceled|Cancelled|Under Contract)\s+(\d{2}\/\d{2}\/\d{2,4})/i;

export function parseHistory(text) {
  const byMls = {};
  String(text || '').split('\n').forEach((line) => {
    const m = line.match(HISTORY_ROW);
    if (!m) return;
    const [, mls, dom, price, type, date] = m;
    if (!byMls[mls]) byMls[mls] = [];
    byMls[mls].push({ mls, dom: dom === '--' ? null : Number(dom), price: toNumber(price), type, date });
  });
  return byMls;
}

// Runs the adjustments, then drops any comp the agent excluded as an outlier.
export function computeAnalysis(comps, excludedMls, rows, subjectTiers, values) {
  const all = runAnalysis({ comps, rows, subjectTiers, values });
  const out = (c) => excludedMls.includes(c.mls);
  const sold = all.sold.filter((c) => !out(c));
  const pending = all.pending.filter((c) => !out(c));
  const active = all.active.filter((c) => !out(c));
  const excluded = [...all.sold, ...all.pending, ...all.active].filter(out);
  const avgAdjustedSold = sold.length ? sold.reduce((s, c) => s + c.adjusted, 0) / sold.length : 0;
  return { sold, pending, active, excluded, avgAdjustedSold };
}

// Short history summary for one comp: original ask, latest ask, number of cuts, days on market.
export function summarizeHistory(rows) {
  if (!rows || !rows.length) return null;
  const listed = rows.filter((r) => !/sold|rented/i.test(r.type));
  const original = listed.find((r) => /new listing/i.test(r.type));
  const latest = listed[listed.length - 1];
  const cuts = listed.filter((r) => /price decrease/i.test(r.type));
  const dom = Math.max(...rows.map((r) => r.dom ?? 0));
  return {
    dom,
    originalPrice: original ? original.price : null,
    latestPrice: latest ? latest.price : null,
    cuts: cuts.length,
    totalReduction: original && latest ? original.price - latest.price : 0,
  };
}

const daysSince = (mdy) => {
  const m = String(mdy || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return null;
  const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  const start = new Date(year, Number(m[1]) - 1, Number(m[2]));
  return Math.max(0, Math.round((Date.now() - start.getTime()) / 86400000));
};

const shortTag = (address) => String(address || '').replace(/^\d+\s+/, '');
// Same rounded range the client PDF shows: nearest $5,000 center, $5,000 each way, ending in $900 or $400 for optics.
const rangeFor = (list) => { const c = Math.round(list / 5000) * 5000; return { low: c - 5100, high: c + 4900 }; };

const defaultSubjectTiers = (rows) => Object.fromEntries(rows.map((r) => [r.key, r.tiers[0].id]));

const tiersFromRecord = (rec, rows) => {
  const out = {};
  rows.forEach((r) => {
    if (r.found || r.custom) out[r.key] = extrasFor(rec).includes(r.key) ? 'yes' : 'no';
    else out[r.key] = tierFor(r.key, rec) || r.tiers[0].id;
  });
  return out;
};

const defaultValues = (rows) => {
  const v = {};
  rows.forEach((r) => {
    v[r.key] = {};
    r.tiers.forEach((t) => { if (!t.base && t.value !== undefined) v[r.key][t.id] = String(t.value); });
  });
  return v;
};

const linesText = (lines) => (lines.length
  ? lines.map((l) => `${l.amount > 0 ? '+' : '-'}${money(Math.abs(l.amount))} ${l.label}`).join(', ')
  : 'None');

function PriceLadder({ points, recommended }) {
  const values = [...points.map((p) => p.value), recommended].filter((v) => v > 0);
  if (!values.length) return null;
  const min = Math.min(...values) * 0.98;
  const max = Math.max(...values) * 1.02;
  const pos = (v) => ((v - min) / (max - min)) * 100;
  return (
    <div style={{ position: 'relative', height: 110, margin: '24px 0 8px' }}>
      <div style={{ position: 'absolute', top: 48, left: 0, right: 0, height: 4, background: '#e6dccb', borderRadius: 2 }} />
      {points.map((p, i) => (
        <div key={p.key} style={{ position: 'absolute', left: `${pos(p.value)}%`, top: i % 2 ? 0 : 60, transform: 'translateX(-50%)', textAlign: 'center', fontSize: 11, whiteSpace: 'nowrap' }}>
          <div style={{ width: 12, height: 12, borderRadius: 6, background: p.color, margin: '0 auto' }} />
          <div style={{ marginTop: 2 }}>{p.label}</div>
          <div style={{ fontWeight: 700 }}>{money(p.value)}</div>
        </div>
      ))}
      {recommended > 0 && (
        <div style={{ position: 'absolute', left: `${pos(recommended)}%`, top: 36, height: 28, borderLeft: `2px solid ${SWATCH.rec}`, transform: 'translateX(-1px)' }} />
      )}
    </div>
  );
}

function historyCell(h) {
  if (!h) return '—';
  const cuts = h.cuts ? `${h.cuts} cut${h.cuts === 1 ? '' : 's'}${h.totalReduction ? `, ${money(h.totalReduction)} total` : ''}` : 'No cuts';
  return `${h.dom} days · ${cuts}`;
}

function CompResults({ analysis, recommendedList, markupPct, history, client, onToggle }) {
  if (!analysis) {
    return client ? null : <p style={muted}>Upload the MLS PDFs and click Run analysis. The feature questions appear after it runs.</p>;
  }
  const { sold, pending, active } = analysis;
  const h = client ? { ...h2, color: '#1a1a1a', marginTop: 24 } : h2;
  const hist = (c) => summarizeHistory(history?.[c.mls]);
  const activeMin = active.length ? Math.min(...active.map((c) => c.adjusted)) : null;
  const activeMax = active.length ? Math.max(...active.map((c) => c.adjusted)) : null;
  const pts = [
    ...sold.map((c) => ({ key: `s${c.mls}`, value: c.adjusted, label: `Sold ${shortTag(c.address)}`, color: SWATCH.sold })),
    ...active.map((c) => ({ key: `a${c.mls}`, value: c.adjusted, label: `Active ${shortTag(c.address)}`, color: SWATCH.active })),
    ...pending.map((c) => ({ key: `p${c.mls}`, value: c.adjusted, label: `Pending ${shortTag(c.address)}`, color: SWATCH.pending })),
  ];
  return (
    <>
      <h2 style={h}>Closed sales and adjustments</h2>
      <p style={{ ...muted, margin: '0 0 8px' }}>Each sold home is adjusted to your home's features. Positive amounts raise the comp's price; negative amounts lower it.</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={table}>
          <thead><tr><th style={th}>Property</th><th style={th}>Sold</th><th style={th}>Market history</th><th style={th}>Adjustments</th><th style={th}>Adjusted price</th>{onToggle && <th style={th}></th>}</tr></thead>
          <tbody>
            {sold.map((c) => (
              <tr key={c.mls}>
                <td style={td}>{c.address}</td>
                <td style={td}>{money(c.price)}{c.statusDate ? ` (${c.statusDate})` : ''}</td>
                <td style={td}>{historyCell(hist(c))}</td>
                <td style={td}>{linesText(c.lines)}</td>
                <td style={{ ...td, fontWeight: 700 }}>{money(c.adjusted)}</td>
                {onToggle && <td style={td}><button onClick={() => onToggle(c.mls)} style={xBtn} title="Exclude as outlier">✕</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 style={{ ...h, marginTop: 24 }}>Active competition</h2>
      <p style={{ ...muted, margin: '0 0 8px' }}>Homes currently for sale. Shown for context and not included in the average.</p>
      {active.length === 0 ? <p style={muted}>No active homes in this report.</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={table}>
            <thead><tr><th style={th}>Property</th><th style={th}>List price</th><th style={th}>Market history</th><th style={th}>Adjusted price</th><th style={th}>Adjustments</th>{onToggle && <th style={th}></th>}</tr></thead>
            <tbody>
              {active.map((c) => {
                const hh = hist(c);
                return (
                  <tr key={c.mls}>
                    <td style={td}>{c.address}</td>
                    <td style={td}>{money(c.price)}</td>
                    <td style={td}>{hh ? historyCell(hh) : `${daysSince(c.statusDate) ?? '—'} days listed`}</td>
                    <td style={td}>{money(c.adjusted)}</td>
                    <td style={td}>{linesText(c.lines)}</td>
                    {onToggle && <td style={td}><button onClick={() => onToggle(c.mls)} style={xBtn} title="Exclude as outlier">✕</button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h2 style={{ ...h, marginTop: 24 }}>Pending</h2>
      <p style={{ ...muted, margin: '0 0 8px' }}>A pending home's adjusted price shows what your home would need to be priced at to match it. It is a check, not part of the average, and an asking price does not set the final sale price.</p>
      {pending.length === 0 ? <p style={muted}>No pending homes in this report.</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={table}>
            <thead><tr><th style={th}>Property</th><th style={th}>List price</th><th style={th}>Adjusted price</th><th style={th}>Against recommended list</th>{onToggle && <th style={th}></th>}</tr></thead>
            <tbody>
              {pending.map((c) => {
                const gap = recommendedList > 0 ? c.adjusted - recommendedList : null;
                return (
                  <tr key={c.mls}>
                    <td style={td}>{c.address}</td>
                    <td style={td}>{money(c.price)}</td>
                    <td style={td}>{money(c.adjusted)}</td>
                    <td style={td}>{gap === null ? '—' : `${money(Math.abs(gap))} ${gap >= 0 ? 'above' : 'below'}`}</td>
                    {onToggle && <td style={td}><button onClick={() => onToggle(c.mls)} style={xBtn} title="Exclude as outlier">✕</button></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {analysis.excluded && analysis.excluded.length > 0 && (
        <>
          <h2 style={{ ...h, marginTop: 24 }}>Excluded as outliers</h2>
          <p style={{ ...muted, margin: '0 0 8px' }}>The agent excluded these homes as outliers. They are not used in the average, the pending check, or the price range.</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={table}>
              <tbody>
                {analysis.excluded.map((c) => (
                  <tr key={c.mls}>
                    <td style={{ ...td, textDecoration: 'line-through', color: '#7f7f7f' }}>{c.address}</td>
                    <td style={{ ...td, color: '#7f7f7f' }}>{c.status}</td>
                    <td style={{ ...td, color: '#7f7f7f' }}>{money(c.price)}</td>
                    <td style={{ ...td, color: '#7f7f7f' }}>Outlier, excluded by agent</td>
                    {onToggle && <td style={td}><button onClick={() => onToggle(c.mls)} style={xBtn}>Reinstate</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2 style={{ ...h, marginTop: 24 }}>Pricing position</h2>
      <div style={{ fontSize: 14, lineHeight: 1.6 }}>
        <div>Average adjusted sold price: {sold.length ? money(analysis.avgAdjustedSold) : 'needs at least one sold comp'}</div>
        <div>Recommended list price ({markupPct}% above the average): {recommendedList > 0 ? money(recommendedList) : '—'}</div>
        <div>Recommended list price range (client PDF): {recommendedList > 0 ? `${money(rangeFor(recommendedList).low)} to ${money(rangeFor(recommendedList).high)}` : '—'}</div>
        {activeMin !== null && <div>Active homes adjusted to your home range from {money(activeMin)} to {money(activeMax)}.</div>}
      </div>
      <PriceLadder points={pts} recommended={recommendedList} />
    </>
  );
}

export default function CmaBuilder() {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [mode, setMode] = useState('internal');
  const [profile, setProfile] = useState(null);

  const [subject, setSubject] = useState({ address: '', previouslyListed: false, sqft: '', beds: '', baths: '', stories: 'One story' });
  const [uploads, setUploads] = useState({ subject: null, comps: null, history: null });
  const [markup, setMarkup] = useState('3');
  const [ratio, setRatio] = useState('');
  const [busy, setBusy] = useState(false);
  const [shareMsg, setShareMsg] = useState('');

  const [compRecords, setCompRecords] = useState([]);
  const [customFeatures, setCustomFeatures] = useState([]);
  const [subjectTiers, setSubjectTiers] = useState({});
  const [values, setValues] = useState({});
  const [history, setHistory] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [analysisMsg, setAnalysisMsg] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [listOverride, setListOverride] = useState('');
  const [excluded, setExcluded] = useState([]);
  const [transactionsRaw, setTransactionsRaw] = useState([]);
  const [clientId, setClientId] = useState('');
  const [sendDialog, setSendDialog] = useState(null);
  // Opened from a Seller Interest (Pipeline): /tools/cma?sellerInterest=<id> prefills the subject and links the CMA back.
  // Or picked from "Choose from my pipeline" below. Pipeline sellers are only ever the SUBJECT, never comps.
  const [sellerInterestId, setSellerInterestId] = useState(() => new URLSearchParams(window.location.search).get('sellerInterest'));
  const [sellerInterest, setSellerInterest] = useState(null);
  // Localhost sample sellers prefill the CMA but are never read from or written to the database.
  const sampleInterest = sellerInterestId && SHOW_SAMPLES ? SAMPLE_PIPELINE.find((x) => x.id === sellerInterestId && x.kind === 'seller') : null;
  const [pipelineSellers, setPipelineSellers] = useState([]);
  // Own pipeline sellers (Admin sees all). Converted sellers are skipped. Reading only; nothing is written here.
  useEffect(() => watchAccessible('sellerInterests', user?.uid, userRole === 'Admin', setPipelineSellers), [user?.uid, userRole]);
  const pickable = useMemo(() => [
    ...pipelineSellers.filter((r) => !(r.status === 'Converted' || r.convertedTransactionId))
      .filter((r) => canSeeRecord(r, user?.uid, userRole === 'Admin')),
    // Sample sellers are for localhost only; on the live site they cluttered this list with fake names.
    ...(SHOW_SAMPLES ? SAMPLE_PIPELINE.filter((x) => x.kind === 'seller') : []),
  ], [pipelineSellers, userRole, user]);
  const choosePipelineSeller = (id) => {
    const si = pickable.find((x) => x.id === id);
    if (!si) { setSellerInterestId(null); setSellerInterest(null); return; }
    setSellerInterestId(si.id); setSellerInterest(si);
    setSubject((prev) => ({ ...prev, address: si.propertyAddress || '', community: si.community || '', floorPlan: si.floorPlan || '', beds: si.beds || '', baths: si.baths || '', sqft: si.sqft || '', pool: si.pool || '', view: si.view || '' }));
    if (!si.sample) linkCmaToInterest(si.id, { ...(si.cma || {}), status: si.cma?.fileName ? 'created' : 'started', address: si.propertyAddress || '' }).catch(() => {});
  };
  const prefill = (si) => setSubject((prev) => ({ ...prev, address: prev.address || si.propertyAddress || '', community: si.community || '', floorPlan: si.floorPlan || '', beds: prev.beds || si.beds || '', baths: prev.baths || si.baths || '', sqft: prev.sqft || si.sqft || '' }));
  useEffect(() => {
    if (!sellerInterestId || sellerInterest?.id === sellerInterestId) return;
    if (sampleInterest) { setSellerInterest(sampleInterest); prefill(sampleInterest); return; }
    getSellerInterest(sellerInterestId).then((si) => {
      if (!ownsInterest(si, user?.uid, userRole)) return;
      setSellerInterest(si);
      prefill(si);
      linkCmaToInterest(sellerInterestId, { ...(si.cma || {}), status: si.cma?.fileName ? 'created' : 'started', address: si.propertyAddress || '' }).catch(() => {});
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerInterestId]);

  useEffect(() => {
    if (!user?.uid) return;
    get(ref(database, `users/${user.uid}`))
      .then((snap) => setProfile(profileFromRecord(snap.val() || {})))
      .catch(() => setProfile(profileFromRecord({})));
  }, [user]);

  // The agent's own transactions only. Each one is a client the agent can send the CMA to.
  useEffect(() => {
    if (!user?.uid) return;
    get(ref(database, `users/${user.uid}/transactions`))
      .then((snap) => {
        const data = snap.val() || {};
        setTransactionsRaw(Object.keys(data).map((id) => ({ id, ...data[id] })));
      })
      .catch(() => setTransactionsRaw([]));
  }, [user]);

  const clientOptions = useMemo(() => transactionsRaw
    .filter((t) => String(t.status || '').toLowerCase() !== 'lost')
    .map((t) => ({
      id: t.id,
      name: t.clientName || `${t.firstName || ''} ${t.lastName || ''}`.trim(),
      email: t.email || '',
      phone: t.phone || '',
      address: t.propertyAddress || t.address || '',
    }))
    .filter((c) => c.name)
    .sort((a, b) => a.name.localeCompare(b.name)), [transactionsRaw]);
  const selectedClient = clientOptions.find((c) => c.id === clientId) || null;

  // Saved CMA drafts: /tools/cma?draft=<id>&owner=<uid> reopens one. Uploaded PDFs are not stored;
  // everything read from them (comps, history, answers, adjustments, notes) is.
  const qs = new URLSearchParams(window.location.search);
  const [draftId, setDraftId] = useState(() => qs.get('draft'));
  const [draftOwner] = useState(() => qs.get('owner'));
  const [draftStatus, setDraftStatus] = useState('draft');
  const [draftNotes, setDraftNotes] = useState('');
  const [draftMsg, setDraftMsg] = useState('');
  const [draftLinks, setDraftLinks] = useState({});
  useEffect(() => {
    if (!draftId || !user?.uid) return;
    const owner = draftOwner || user.uid;
    if (owner !== user.uid && userRole !== 'Admin') { setDraftMsg('That saved CMA belongs to another agent.'); return; }
    dbGet2(dbRef2(database, `cmaDrafts/${owner}/${draftId}`)).then((snap) => {
      const d = snap.val();
      if (!d) { setDraftMsg('That saved CMA was not found.'); return; }
      const st = d.state || {};
      if (st.subject) setSubject((prev) => ({ ...prev, ...st.subject }));
      setCompRecords(st.compRecords || []); setCustomFeatures(st.customFeatures || []);
      setSubjectTiers(st.subjectTiers || {}); setValues(st.values || {}); setHistory(st.history || {});
      setExcluded(st.excluded || []); setMarkup(st.markup ?? '3'); setRatio(st.ratio || ''); setListOverride(st.listOverride || '');
      if (st.clientId) setClientId(st.clientId);
      if (st.sellerInterestId) setSellerInterestId(st.sellerInterestId);
      setDraftNotes(d.notes || ''); setDraftStatus(d.status || 'draft'); setDraftLinks(d.client || {});
      if ((st.compRecords || []).length) setAnalysis(computeAnalysis(st.compRecords, st.excluded || [], featureRows(st.compRecords, st.customFeatures || []), st.subjectTiers || {}, st.values || {}));
      setDraftMsg(`Opened saved CMA (${d.status === 'final' ? 'final' : 'draft'}). Re-upload PDFs only if you want to re-read them.`);
    }).catch(() => setDraftMsg('Could not open that saved CMA.'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, user?.uid]);
  const saveDraft = async (status) => {
    if (!user?.uid) return;
    const tx = transactionsRaw.find((t) => t.id === clientId);
    const si = sellerInterest && !sellerInterest.sample ? sellerInterest : null;
    const links = clientLinks({
      ...draftLinks,
      contactId: si?.contactId || tx?.contactId || draftLinks.contactId,
      sellerInterestId: si?.id || draftLinks.sellerInterestId,
      transactionId: tx?.id || draftLinks.transactionId,
      email: si?.email || tx?.email || draftLinks.email,
      phone: si?.phone || tx?.phone || draftLinks.phone,
      name: (si ? fullName(si) : '') || (tx ? (tx.clientName || `${tx.firstName || ''} ${tx.lastName || ''}`.trim()) : '') || draftLinks.name,
    });
    const owner = draftId && draftOwner ? draftOwner : user.uid;
    try {
      setDraftMsg('Saving...');
      const id = await saveCmaDraft(owner, draftId, {
        status, address: subject.address || '', notes: draftNotes, client: links,
        ownerName: profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : '',
        state: { subject, compRecords, customFeatures, subjectTiers, values, history, excluded, markup, ratio, listOverride, clientId, sellerInterestId: si?.id || null },
      });
      setDraftId(id); setDraftStatus(status); setDraftLinks(links);
      setDraftMsg(`${status === 'final' ? 'Saved as final' : 'Draft saved'} ${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.${links.name ? ` Linked to ${links.name}.` : ' Pick a pipeline seller or a client to link it.'}`);
    } catch (e) { setDraftMsg('Could not save. Please try again.'); }
  };

  const rows = useMemo(() => featureRows(compRecords, customFeatures), [compRecords, customFeatures]);
  const markupPct = num(markup);
  const avgSold = analysis?.avgAdjustedSold || 0;
  const calculatedList = avgSold * (1 + markupPct / 100);
  const overrideValue = toNumber(listOverride);
  const recommendedList = overrideValue !== null && overrideValue > 0 ? overrideValue : calculatedList;
  const listEdited = overrideValue !== null && overrideValue > 0;
  // Range is the recommended price rounded to the nearest $5,000, plus or minus $5,000,
  // with $100 taken off each end so it reads like 779,900 or 999,900.
  const roundedCenter = Math.round(recommendedList / 5000) * 5000;
  const priceRangeLow = roundedCenter - 5000 - 100;
  const priceRangeHigh = roundedCenter + 5000 - 100;
  const agentFirst = profile?.firstName || '';
  // Falls back to the sign-in name or email, so a PDF from an unfinished profile never has a blank agent block.
  const agentFull = (profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : '') || user?.displayName || '';
  const fileName = `CMA - ${(subject.address || 'Subject property').replace(/[\\/:*?"<>|]/g, '')}.pdf`;
  const clientMessage = `Your comparative market analysis for ${subject.address || 'your home'} is attached. The quickest way to go over it together is a call or text: ${profile?.phone || ''}. - ${agentFirst}`;
  const missing = analysis ? missingValues(rows, subjectTiers, values) : [];

  const setSub = (k) => (e) => setSubject({ ...subject, [k]: e.target.value });
  const onFile = (k) => (e) => setUploads({ ...uploads, [k]: e.target.files?.[0] || null });

  const UploadRow = ({ k, text }) => (
    <div style={{ marginBottom: 10 }}>
      <span style={label}>{text}</span>
      <input type="file" accept="application/pdf" onChange={onFile(k)} style={{ ...input, padding: '6px 8px', background: '#fff' }} />
      {uploads[k] && <span style={{ ...muted, display: 'block', marginTop: 4 }}>Attached: {uploads[k].name}</span>}
    </div>
  );

  // Step 1: read every uploaded MLS PDF, find features in the descriptions, then show the questions.
  const runInitialAnalysis = async () => {
    if (!uploads.comps) { setAnalysisMsg('Upload the MLS comps PDF first.'); return; }
    setBusy(true);
    setAnalysisMsg('Reading the MLS reports...');
    try {
      const comps = splitRecords(await readPdfText(uploads.comps)).map(readRecord).filter((r) => r.mls);
      const subjRec = subject.previouslyListed && uploads.subject
        ? splitRecords(await readPdfText(uploads.subject)).map(readRecord)[0]
        : null;
      const newRows = featureRows(comps, []);
      const tiers = { ...defaultSubjectTiers(newRows), ...(subjRec ? tiersFromRecord(subjRec, newRows) : {}) };
      const vals = defaultValues(newRows);
      let hist = {};
      if (uploads.history) hist = parseHistory(await readPdfText(uploads.history));
      setCompRecords(comps);
      setCustomFeatures([]);
      setListOverride('');
      if (subjRec?.address && !subject.address) setSubject({ ...subject, address: subjRec.address });
      setSubjectTiers(tiers);
      setValues(vals);
      setHistory(hist);
      setExcluded([]);
      setAnalysis(computeAnalysis(comps, [], newRows, tiers, vals));
      setAnalysisMsg(`Read ${comps.length} records: ${comps.filter((c) => /sold/i.test(c.status)).length} sold, ${comps.filter((c) => /pending/i.test(c.status)).length} pending, ${comps.filter((c) => /active/i.test(c.status)).length} active. Answer the feature questions below, then click Update analysis.`);
    } catch (err) {
      setAnalysisMsg(err.message || 'Could not read the MLS reports.');
    } finally {
      setBusy(false);
    }
  };

  // Step 2: rerun with the agent's answers. No PDFs are read again.
  const updateAnalysis = () => {
    setAnalysis(computeAnalysis(compRecords, excluded, rows, subjectTiers, values));
    setAnalysisMsg('Analysis updated with the feature values.');
  };

  const toggleExclude = (mls) => {
    const next = excluded.includes(mls) ? excluded.filter((m) => m !== mls) : [...excluded, mls];
    setExcluded(next);
    setAnalysis(computeAnalysis(compRecords, next, rows, subjectTiers, values));
  };

  const toggleCustomComp = (key, mls) => {
    const next = compRecords.map((c) => (c.mls === mls ? { ...c, customYes: { ...(c.customYes || {}), [key]: !c.customYes?.[key] } } : c));
    setCompRecords(next);
    setAnalysis(computeAnalysis(next, excluded, rows, subjectTiers, values));
  };

  const setTier = (key, tierId) => setSubjectTiers({ ...subjectTiers, [key]: tierId });
  const setValue = (key, tierId, text) => setValues({ ...values, [key]: { ...(values[key] || {}), [tierId]: text } });

  const addFeature = () => {
    const name = newFeature.trim();
    if (!name) return;
    const key = `c_${Date.now()}`;
    const words = name.toLowerCase().replace(/[^a-z]/g, '');
    // Start each comp with a guess from its description; the agent can change any of them.
    const next = compRecords.map((c) => {
      const desc = (c.description || '').toLowerCase().replace(/[^a-z]/g, '');
      return { ...c, customYes: { ...(c.customYes || {}), [key]: words.length > 0 && desc.includes(words) } };
    });
    const nextCustom = [...customFeatures, { key, label: name }];
    const nextTiers = { ...subjectTiers, [key]: 'no' };
    setCompRecords(next);
    setCustomFeatures(nextCustom);
    setSubjectTiers(nextTiers);
    setAnalysis(computeAnalysis(next, excluded, featureRows(next, nextCustom), nextTiers, values));
    setNewFeature('');
    setAnalysisMsg(`Added "${name}" to every comp. Enter its value and check which comps have it.`);
  };

  const getPdf = async () => {
    setBusy(true);
    setShareMsg('Preparing PDF...');
    try {
      const el = document.getElementById('client-cma');
      const html2canvas = await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js', 'html2canvas');
      const jspdf = await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js', 'jspdf');
      const scale = 2;
      const canvas = await html2canvas(el, { scale, backgroundColor: '#ffffff', useCORS: true });
      const pdf = new jspdf.jsPDF({ unit: 'pt', format: 'letter' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 36;
      const contentW = pageW - margin * 2;
      const pxPerPt = canvas.width / contentW;
      const pageHpx = Math.floor((pageH - margin * 2) * pxPerPt);
      // Pages break only between table rows or section headings, never through the middle of a line.
      const topEdge = el.getBoundingClientRect().top;
      const breaks = Array.from(el.querySelectorAll('h2, tr'))
        .map((n) => (n.getBoundingClientRect().top - topEdge) * scale)
        .filter((b) => b > 0)
        .sort((a, b) => a - b);
      let sy = 0;
      let first = true;
      while (sy < canvas.height) {
        let stop = Math.min(sy + pageHpx, canvas.height);
        if (stop < canvas.height) {
          const cuts = breaks.filter((b) => b > sy + pageHpx * 0.5 && b <= stop);
          if (cuts.length) stop = cuts[cuts.length - 1];
        }
        const sliceH = Math.round(stop - sy);
        const part = document.createElement('canvas');
        part.width = canvas.width;
        part.height = sliceH;
        part.getContext('2d').drawImage(canvas, 0, sy, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        if (!first) pdf.addPage();
        first = false;
        pdf.addImage(part.toDataURL('image/png'), 'PNG', margin, margin, contentW, sliceH / pxPerPt);
        sy = stop;
      }
      setShareMsg('');
      return new File([pdf.output('blob')], fileName, { type: 'application/pdf' });
    } catch (err) {
      setShareMsg(err.message || 'Could not create the PDF.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    const file = await getPdf();
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setShareMsg('PDF downloaded.');
    if (sellerInterestId && !sampleInterest) {
      linkCmaToInterest(sellerInterestId, { status: 'created', fileName, address: subject.address || '', recommendedList: recommendedList ? '$' + Math.round(recommendedList).toLocaleString('en-US') : '' })
        .then(() => setShareMsg('PDF downloaded and linked to the seller interest.')).catch(() => {});
    }
  };

  const firstNameOf = (name) => (name || '').split(' ')[0];
  const defaultMessage = (client, channel) => `Hi ${firstNameOf(client.name)}, I've done an analysis on your property and I've ${channel === 'text' ? 'linked' : 'attached'} it below. I'd love the opportunity to review it together.\n\n${agentFirst}${profile?.phone ? `\nCall or text: ${profile.phone}` : ''}`;
  const recipientFor = (client, channel) => (channel === 'text' ? client?.phone || '' : client?.email || '');
  const canSend = (channel) => !!selectedClient && !!subject.address.trim() && !!recipientFor(selectedClient, channel);

  const openSend = (channel) => {
    setShareMsg('');
    setSendDialog({ channel, body: defaultMessage(selectedClient, channel) });
  };

  const confirmSend = async () => {
    const { channel, body } = sendDialog;
    setBusy(true);
    setShareMsg('Preparing PDF...');
    try {
      const file = await getPdf();
      if (!file) return;
      // Save the CMA to this client's transaction documents first, so the link points to a stored copy.
      let saved;
      try {
        saved = await uploadDealDocument(user.uid, selectedClient.id, file, agentFull || user.uid);
      } catch (err) {
        console.error('Saving the CMA to the transaction failed:', err);
        setShareMsg('Could not save the PDF to this client\'s transaction. Nothing was sent.');
        return;
      }
      const finalBody = channel === 'text' ? `${body}\n${saved.url}` : body;
      const res = await sendCmaToClient({ channel, to: recipientFor(selectedClient, channel), body: finalBody, file, fileName });
      if (res.sent) {
        setShareMsg(`Sent to ${selectedClient.name}.`);
        setSendDialog(null);
        return;
      }
      const messages = {
        sending_disabled: 'Sending is turned off until your text and email service is connected.',
        service_not_connected: 'Sending is not connected yet. The text and email service still needs to be turned on.',
        no_recipient: 'No phone or email is saved for this client.',
        send_failed: 'The message did not send. Try again.',
      };
      setShareMsg(messages[res.reason] || 'The message did not send.');
    } finally {
      setBusy(false);
    }
  };

  const subjectFeatureSummary = rows
    .filter((r) => { const t = subjectTiers[r.key] ?? r.tiers[0].id; return t !== r.tiers[0].id; })
    .map((r) => `${r.label}: ${r.tiers.find((x) => x.id === (subjectTiers[r.key] ?? r.tiers[0].id))?.label || ''}`);

  if (mode === 'client') {
    return (
      <div style={page}>
        <div style={wrap} className="no-print">
        {sellerInterest && (
          <div style={{ background: '#FBF5E3', border: '1px solid #D4AF37', borderRadius: 8, padding: 12, marginBottom: 12, color: '#1a1a1a' }}>
            CMA for seller interest: <strong>{[sellerInterest.firstName, sellerInterest.lastName].filter(Boolean).join(' ') || sellerInterest.name}</strong>
            {sellerInterest.community ? ' - ' + sellerInterest.community : ''}{sellerInterest.floorPlan ? ' - ' + sellerInterest.floorPlan + ' plan' : ''}{priceRange(sellerInterest.priceMin, sellerInterest.priceMax) ? ' - seller ballpark ' + priceRange(sellerInterest.priceMin, sellerInterest.priceMax) : ''}.
            {' '}{sampleInterest ? 'Sample record: nothing is saved or linked.' : 'Downloading the PDF links it to the interest.'} <button onClick={() => navigate('/pipeline')} style={{ marginLeft: 8, minHeight: 36 }}>Back to Pipeline</button>
          </div>
        )}
          <button onClick={() => setMode('internal')} style={backBtn}>← Back to working view</button>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <button onClick={() => window.print()} style={btnOutline}>Print or save as PDF</button>
          </div>
        </div>

        <div id="client-cma" style={{ background: '#ffffff', maxWidth: 800, margin: '0 auto', padding: '32px 28px', border: '1px solid #d8c9a8', borderRadius: 8, color: '#1a1a1a' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', borderBottom: '2px solid #D4AF37', paddingBottom: 16 }}>
            <img src="/residence-exp-logo.png" alt="RESIDENCE | eXp Realty" style={{ height: 56, width: 'auto' }} />
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, color: '#7f7f7f' }}>Comparative Market Analysis</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{subject.address || 'Subject property'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '20px 0', flexWrap: 'wrap' }}>
            {profile?.profilePhotoUrl && <div role="img" aria-label={agentFull} style={{ width: 88, height: 88, minWidth: 88, borderRadius: '50%', backgroundImage: `url(${profile.profilePhotoUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />}
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{agentFull}</div>
              {profile?.title && <div>{profile.title}</div>}
              <div>RESIDENCE | eXp Realty</div>
              {profile?.phone && <div>Call or text: {profile.phone}</div>}
              {profile?.contactEmail && <div>{profile.contactEmail}</div>}
            </div>
          </div>

          <h2 style={{ ...h2, color: '#1a1a1a' }}>Your home</h2>
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            {subject.sqft && <div>Living area: {num(subject.sqft).toLocaleString('en-US')} sq ft</div>}
            {(subject.beds || subject.baths) && <div>{subject.beds || '—'} bedrooms, {subject.baths || '—'} bathrooms</div>}
            {subject.stories && <div>{subject.stories}</div>}
            {subjectFeatureSummary.length > 0 && <div>Features: {subjectFeatureSummary.join('; ')}</div>}
          </div>

          <h2 style={{ ...h2, color: '#1a1a1a', marginTop: 24 }}>Comparable homes</h2>
          {analysis ? <CompResults analysis={analysis} recommendedList={recommendedList} markupPct={markupPct} history={history} client />
            : <p style={{ fontSize: 14, color: '#7f7f7f' }}>No comparable homes yet. Go back to the working view, upload the MLS PDFs and click Run analysis.</p>}

          <h2 style={{ ...h2, color: '#1a1a1a', marginTop: 24 }}>Recommended list price</h2>
          <div style={bigRow}><span>Recommended list price range</span><span>{recommendedList > 0 ? `${money(priceRangeLow)} to ${money(priceRangeHigh)}` : '—'}</span></div>
          <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
            The recommended list price sits about {markupPct}% above the average adjusted sold price of the comparable homes to leave room for negotiation.
            {num(ratio) > 0 && avgSold > 0 && <> Based on the community's average sold-to-list ratio of {ratio}%, the list price that would produce that average sale is about {money(avgSold * 100 / num(ratio))}.</>}
          </p>

          <p style={{ fontSize: 12, color: '#7f7f7f', lineHeight: 1.5, marginTop: 24 }}>
            This comparative market analysis is an estimate based on the MLS data provided. It is not an appraisal. Market conditions change, and the final price depends on the home's condition, upgrades, and current demand.
          </p>

          <div style={{ marginTop: 20, paddingTop: 12, borderTop: '1px solid #d8c9a8', fontSize: 14 }}>
            <div style={{ fontWeight: 700 }}>{agentFull}</div>
            {profile?.title && <div>{profile.title} | RESIDENCE | eXp Realty</div>}
            {profile?.phone && <div>Call or text: {profile.phone}</div>}
            {profile?.contactEmail && <div>{profile.contactEmail}</div>}
          </div>
        </div>

        <div className="no-print" style={{ maxWidth: 800, margin: '16px auto 0', display: 'grid', gap: 10 }}>
          <button onClick={downloadPdf} disabled={busy || !subject.address.trim()} style={{ ...btnGold, padding: '12px 16px', fontSize: '1rem', ...(busy || !subject.address.trim() ? btnDisabled : {}) }}>Download client PDF</button>
          <div style={{ display: 'grid', gap: 6 }}>
            <span style={label}>Send to client</span>
            <select style={input} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Choose a client</option>
              {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}{c.address ? ` (${c.address})` : ''}</option>)}
            </select>
            {selectedClient && (
              <span style={muted}>{selectedClient.phone ? `Phone: ${selectedClient.phone}` : 'No phone saved.'} {selectedClient.email ? `· ${selectedClient.email}` : '· No email saved.'}</span>
            )}
            {clientOptions.length === 0 && <span style={muted}>No clients are assigned to your account yet.</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <button onClick={() => openSend('text')} disabled={busy || !canSend('text')} style={{ ...btnGold, padding: '12px 16px', fontSize: '1rem', ...(busy || !canSend('text') ? btnDisabled : {}) }}>Text to Client</button>
            <button onClick={() => openSend('email')} disabled={busy || !canSend('email')} style={{ ...btnOutline, padding: '12px 16px', fontSize: '1rem', ...(busy || !canSend('email') ? btnDisabled : {}) }}>Email to Client</button>
          </div>
          {sendDialog && selectedClient && (
            <div style={{ ...card, border: '1px solid #D4AF37', display: 'grid', gap: 10 }}>
              <h2 style={h2}>{sendDialog.channel === 'text' ? 'Text' : 'Email'} to {selectedClient.name}</h2>
              <div style={muted}>To: {recipientFor(selectedClient, sendDialog.channel)}</div>
              <textarea style={{ ...input, minHeight: 110, fontFamily: 'inherit' }} value={sendDialog.body} onChange={(e) => setSendDialog({ ...sendDialog, body: e.target.value })} />
              <div style={muted}>{sendDialog.channel === 'text' ? 'The PDF is saved to this client\'s transaction documents, and the link is added to the text.' : `Attached: ${fileName}. A copy is also saved to this client's transaction documents.`}</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={confirmSend} disabled={busy} style={{ ...btnGold, ...(busy ? btnDisabled : {}) }}>Send</button>
                <button onClick={() => setSendDialog(null)} style={btnOutline}>Cancel</button>
              </div>
            </div>
          )}
          {!subject.address.trim() && <div style={{ ...muted, textAlign: 'center', margin: 0, color: '#8C6D1F' }}>Enter the subject property address in the Working view so it prints on the PDF.</div>}
          {shareMsg && <div style={{ ...muted, textAlign: 'center', margin: 0 }}>{shareMsg}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <div style={wrap}>
        <button onClick={() => navigate('/tools')} style={backBtn}>← Back to tools</button>
        <h1 style={h1}>CMA Builder</h1>
        <p style={muted}>Upload the MLS PDFs and enter the subject details. The feature questions appear after the first analysis, and the numbers update when you click Update analysis.</p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button onClick={() => setMode('internal')} style={mode === 'internal' ? btnGold : btnOutline}>Working view</button>
          <button onClick={() => setMode('client')} style={mode === 'client' ? btnGold : btnOutline}>Client PDF preview</button>
        </div>

        <div style={card}>
          <h2 style={h2}>Subject property</h2>
            {pickable.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <span style={label}>Fill in from a seller in my pipeline (optional)</span>
                <select style={input} value={sellerInterestId || ''} onChange={(e) => choosePipelineSeller(e.target.value)} aria-label="Choose from my pipeline">
                  <option value="">No, I'll type the address below</option>
                  {pickable.map((x) => <option key={x.id} value={x.id}>{fullName(x) || 'Seller'}{x.propertyAddress ? ` - ${x.propertyAddress}` : ''}{x.sample ? ' (Sample)' : ''}</option>)}
                </select>
              </div>
            )}
          <div style={{ marginBottom: 12 }}>
            <span style={label}>Address</span>
            <input style={input} value={subject.address} onChange={setSub('address')} placeholder="123 Example Blvd, Estero" />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 14 }}>
            <input type="checkbox" checked={subject.previouslyListed} onChange={(e) => setSubject({ ...subject, previouslyListed: e.target.checked })} />
            This home was listed before (upload its MLS sheet below)
          </label>
          {!subject.previouslyListed && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div><span style={label}>Living area (sq ft)</span><input style={input} type="number" value={subject.sqft} onChange={setSub('sqft')} /></div>
              <div><span style={label}>Bedrooms</span><input style={input} type="number" value={subject.beds} onChange={setSub('beds')} /></div>
              <div><span style={label}>Bathrooms</span><input style={input} type="number" step="0.5" value={subject.baths} onChange={setSub('baths')} /></div>
              <div>
                <span style={label}>Stories</span>
                <select style={input} value={subject.stories} onChange={setSub('stories')}>
                  <option>One story</option>
                  <option>Two story</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div style={card}>
          <h2 style={h2}>Upload MLS PDFs</h2>
          {subject.previouslyListed && <UploadRow k="subject" text="MLS sheet for the subject property (PDF)" />}
          <UploadRow k="comps" text="MLS comps: active, pending, and sold (PDF)" />
          <UploadRow k="history" text="Property history pages for the comps (PDF)" />
          <p style={{ ...muted, marginBottom: 0 }}>Uploaded files are used for this session only and are not saved to Resi yet.</p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            <button onClick={runInitialAnalysis} disabled={busy || !uploads.comps} style={{ ...btnGold, padding: '12px 22px', fontSize: '1rem', boxShadow: '0 2px 6px rgba(184, 150, 46, 0.35)', ...(busy || !uploads.comps ? btnDisabled : {}) }}>Run analysis</button>
            {analysisMsg && <span style={muted}>{analysisMsg}</span>}
          </div>
        </div>

        {analysis && (
          <div style={card}>
            <h2 style={h2}>Feature values</h2>
            <p style={{ ...muted, marginTop: 0 }}>The features below come from the comp descriptions and your subject details. Set each one for your home. Values start filled in; change any that don't fit this property.</p>

            {rows.map((r) => {
              const tier = subjectTiers[r.key] ?? r.tiers[0].id;
              const needsValue = missing.includes(r.label);
              return (
                <div key={r.key} style={{ padding: '10px 0', borderBottom: '1px solid #f0ebe0', display: 'grid', gridTemplateColumns: 'minmax(180px, 1.3fr) minmax(160px, 1fr) minmax(120px, 1fr)', gap: 10, alignItems: 'center', background: needsValue ? '#fff8e1' : 'transparent' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.label}</div>
                    {r.found && <div style={{ fontSize: 12, color: '#8C6D1F' }}>Found in comp descriptions</div>}
                  </div>
                  <div>
                    <span style={label}>Your home</span>
                    <select style={input} value={tier} onChange={(e) => setTier(r.key, e.target.value)}>
                      {r.tiers.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    {r.tiers.filter((t) => !t.base).map((t) => (
                      <div key={t.id} style={{ marginBottom: 4 }}>
                        <span style={label}>{t.label} ($)</span>
                        <input style={input} type="number" step="500" value={values[r.key]?.[t.id] ?? ''} onChange={(e) => setValue(r.key, t.id, e.target.value)} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {rows.filter((r) => r.custom).map((r) => (
              <div key={`cc-${r.key}`} style={{ padding: '10px 0', borderBottom: '1px solid #f0ebe0' }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{r.label}: comps whose descriptions mention it</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
                  {compRecords.filter((c) => c.customYes?.[r.key]).length === 0 && <span style={muted}>No comp description mentions this, so no comp is adjusted for it.</span>}
                  {compRecords.filter((c) => c.customYes?.[r.key]).map((c) => (
                    <label key={c.mls} style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="checkbox" checked={!!c.customYes?.[r.key]} onChange={() => toggleCustomComp(r.key, c.mls)} />
                      {shortTag(c.address)} ({c.status})
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              <input style={{ ...input, maxWidth: 280 }} value={newFeature} onChange={(e) => setNewFeature(e.target.value)} placeholder="Add another feature (for example, Accent wall)" />
              <button onClick={addFeature} style={btnOutline}>Add feature</button>
            </div>

            {missing.length > 0 && <p style={{ ...muted, color: '#8C6D1F', marginTop: 12 }}>Enter a value for: {missing.join(', ')}</p>}

            <div style={{ marginTop: 14 }}>
              <button onClick={updateAnalysis} disabled={busy} style={btnGold}>Update analysis</button>
            </div>
          </div>
        )}

        <div style={card}>
          <h2 style={h2}>Pricing</h2>
          <div style={row}><span>Average adjusted sold price</span><span>{avgSold > 0 ? money(avgSold) : 'Available after the sold comps are read'}</span></div>
          <div style={{ ...row, alignItems: 'center' }}>
            <span>Community average sold-to-list ratio (%)</span>
            <input style={{ ...input, maxWidth: 120 }} type="number" step="0.1" value={ratio} onChange={(e) => setRatio(e.target.value)} placeholder="e.g. 97" />
          </div>
          <div style={{ ...row, alignItems: 'center' }}>
            <span>Markup above average (%)</span>
            <input style={{ ...input, maxWidth: 120 }} type="number" step="0.5" value={markup} onChange={(e) => setMarkup(e.target.value)} />
          </div>
          <div style={{ ...row, alignItems: 'center' }}>
            <span>Recommended list price {listEdited ? '(edited by agent)' : ''}</span>
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input style={{ ...input, maxWidth: 160, fontWeight: 700 }} type="text" inputMode="numeric" value={listEdited ? money(overrideValue) : (avgSold > 0 ? money(Math.round(calculatedList)) : '')} onChange={(e) => setListOverride(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Run analysis first" />
              {listEdited && <button onClick={() => setListOverride('')} style={btnOutline}>Reset to calculated</button>}
            </span>
          </div>
          <div style={row}><span>Client PDF price range</span><span>{avgSold > 0 ? `${money(priceRangeLow)} to ${money(priceRangeHigh)}` : '—'}</span></div>
          <p style={{ ...muted, marginTop: 8, marginBottom: 0 }}>Calculated from the adjusted sold average plus {markupPct}%: {avgSold > 0 ? money(calculatedList) : '—'}. Any edit here is what appears on the client PDF.</p>
        </div>

        {analysis && (
          <div style={card}>
            <CompResults analysis={analysis} recommendedList={recommendedList} markupPct={markupPct} history={history} onToggle={toggleExclude} />
          </div>
        )}

        {/* Save status and notes sit at the bottom, after the work (Kelly, Nov 3 demo). */}
        <div style={{ ...card, borderColor: '#D4AF37' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <strong style={{ marginRight: 'auto' }}>{draftId ? `Saved CMA · ${draftStatus === 'final' ? 'Final' : 'Draft'}` : 'Not saved yet'}</strong>
            <button onClick={() => saveDraft('draft')} style={btnOutline}>Save draft</button>
            <button onClick={() => saveDraft('final')} style={btnGold}>Save as final</button>
          </div>
          <span style={{ ...label, marginTop: 10 }}>Notes (saved with the CMA)</span>
          <textarea style={{ ...input, minHeight: 60 }} value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} />
          {draftMsg && <div style={{ ...muted, marginTop: 6 }}>{draftMsg}</div>}
        </div>
      </div>
    </div>
  );
}
